import React, { Component, ComponentClass, ComponentType } from "react";
import { connect } from "react-redux";
import { Location } from "redux-first-router";
import { compose } from "redux";
import getDisplayName from "react-display-name";

import { RootState } from "../ducks/reducers";
import { RouteConfig } from "../appConfig/types";
import { getLocation } from "../ducks/location/selectors";

type LocationProp = {
  location: Location;
};

type RouteProp = {
  route: RouteConfig;
  name: string;
};

type StateWithLocation = {
  location: Location;
};

type WithStateLocation = <OriginalProps extends LocationProp>(
  EnhancedComponent: ComponentType<OriginalProps>,
) => ComponentClass<OriginalProps>;

type WithLocation = <OriginalProps>(
  EnhancedComponent: ComponentType<OriginalProps>,
) => ComponentClass<OriginalProps & RouteProp>;

export const withStateLocation = connect<LocationProp, void, void, StateWithLocation>(state => ({
  location: getLocation(state as RootState),
})) as unknown as WithStateLocation;

export function comparePayloads(
  routePayload: Record<string, unknown>,
  locationPayload: Record<string, unknown>,
  isDefault: boolean,
): boolean {
  return Object.entries(routePayload).every(
    ([key, value]) =>
      (isDefault && !locationPayload[key]) ||
      (Array.isArray(value) ? value.includes(locationPayload[key]) : value === locationPayload[key]),
  );
}

function withLocationBase<OriginalProps extends RouteProp>(
  EnhancedComponent: ComponentType<OriginalProps>,
): ComponentClass<OriginalProps> {
  return class extends Component<OriginalProps & LocationProp> {
    static displayName = `withLocation(${getDisplayName(EnhancedComponent)})`;

    render() {
      const { location, route, ...props } = this.props as RouteProp & LocationProp;

      const condition =
        typeof route === "string"
          ? route === location.type
          : route.name === location.type &&
            (!route.payload || comparePayloads(route.payload, location.payload, route.default));

      // TODO: find a way to remove whole HOC
      return <EnhancedComponent render={condition} route={route} {...(props as OriginalProps)} {...location.payload} />;
    }
  };
}

// How to delete that hoc?
export const withLocation = compose(withStateLocation, withLocationBase) as WithLocation;
